/* Off the road and back (2026-10-28): the classic dashboard's suspend /
   maintenance with reassignment, and "Return to active", in the console.

   Taking a bus off the road moves every child on it to another bus first, as
   classic did: the bus keeps its list as `parkedRoster` (with `parkedRosterMoves`,
   child → the bus they went to), each target bus gains them, and each parent's
   record points at the new bus. Back on the road reverses it. Every write is
   one the rules let a fleet admin make (buses: children + status + the parked
   fields; users: children / busChildren). The change is logged in
   busStatusEvents, which the Alerts page reads.

   Parents are told through the payments backend's bus announcement
   (/api/fleet/announce), which messages whoever is on the bus's list — so it
   goes out while the children are still on it: before the move, after the
   return. (Classic called notifyFleetRosterParents on an EU functions instance
   where nothing is deployed, so its parents were never told.) Classic's other
   option, a hired bus, called a function that was never deployed either; it is
   not offered. */
import {
  deleteField, doc, getDoc, serverTimestamp, Timestamp, writeBatch,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { announceToBusParents } from '../../services/fleetContactApi';
import { logBusStatusEvent } from '../../fleet/busStatusEvents';
import type { Bus, FleetModel } from './model';

export type OffReason = 'maintenance' | 'suspended';
type Raw = Record<string, unknown>;
const str = (v: unknown) => (typeof v === 'string' ? v : '');

/** Point each parent's child records at `busFor(childId)`. */
async function repointParents(batch: ReturnType<typeof writeBatch>, kids: Raw[], busFor: (childId: string) => string | null) {
  const parents = [...new Set(kids.map((c) => str(c.parentId)).filter(Boolean))];
  for (const parentId of parents) {
    // eslint-disable-next-line no-await-in-loop
    const snap = await getDoc(doc(db, 'users', parentId));
    if (!snap.exists()) continue;
    const data = snap.data() as Raw;
    let rows = Array.isArray(data.children) ? [...(data.children as Raw[])] : [];
    const busChildren = { ...((data.busChildren as Record<string, string>) || {}) };
    kids.filter((c) => str(c.parentId) === parentId).forEach((c) => {
      const cid = str(c.childId);
      const name = str(c.childName).trim();
      const to = busFor(cid);
      if (!to) return;
      if (name) busChildren[name] = to;
      rows = rows.map((r) => (str(r.childId) === cid || (name && str(r.childName) === name) ? { ...r, busId: to } : r));
    });
    batch.update(doc(db, 'users', parentId), { children: rows, busChildren, updatedAt: serverTimestamp() });
  }
}

/** How a change was made. Zana's takeover tells the parents in a step of its
    own, after the owner has read the message, so it passes announce: false. */
export interface StatusOpts { announce?: boolean; source?: 'dashboard' | 'zana' }

export async function takeOffRoad(
  f: FleetModel, bus: Bus, reason: OffReason, moves: Record<string, string>, by: { uid: string | null; email: string | null },
  opts: StatusOpts = {},
): Promise<{ moved: number; told: boolean }> {
  const ref = doc(db, 'buses', bus.id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('That bus is no longer on Trasse.');
  const kids = ((snap.data().children as Raw[]) || []).filter((c) => c && typeof c === 'object');
  const missing = kids.filter((c) => !moves[str(c.childId)]);
  if (missing.length) throw new Error(`Choose a bus for ${missing.length === 1 ? str(missing[0].childName) || 'one child' : `${missing.length} children`} first.`);

  const byTarget = new Map<string, Raw[]>();
  kids.forEach((c) => {
    const to = moves[str(c.childId)];
    byTarget.set(to, [...(byTarget.get(to) || []), { ...c, status: 'not_picked', lastUpdated: Timestamp.now(), timestamp: null }]);
  });
  const batch = writeBatch(db);
  for (const [to, adds] of byTarget) {
    // eslint-disable-next-line no-await-in-loop
    const t = await getDoc(doc(db, 'buses', to));
    if (!t.exists()) throw new Error('One of the buses you chose is no longer on Trasse.');
    const ids = new Set(adds.map((c) => str(c.childId)));
    const kept = ((t.data().children as Raw[]) || []).filter((c) => !ids.has(str(c.childId)));
    batch.update(doc(db, 'buses', to), { children: [...kept, ...adds], updatedAt: serverTimestamp() });
  }
  batch.update(ref, { children: [], status: reason, parkedRoster: kids, parkedRosterMoves: moves, updatedAt: serverTimestamp() });
  await repointParents(batch, kids, (cid) => moves[cid] || null);

  // Told first, while the children are still on this bus's list.
  let told = false;
  if (kids.length && opts.announce !== false) {
    const word = reason === 'maintenance' ? 'is off the road for maintenance' : 'is off the road for now';
    const r = await announceToBusParents(f.org.code, bus.id, `${bus.name} ${word}. Your child rides another bus until it is back — the Trasse app shows which.`).catch(() => null);
    told = !!r?.ok;
  }
  await batch.commit();
  void logBusStatusEvent({
    busId: bus.id, orgCode: f.org.code, plate: bus.plate, busName: bus.name, fromStatus: str(bus.raw.status) || 'active', toStatus: reason,
    byUid: by.uid, byEmail: by.email, source: opts.source || 'dashboard', childCount: kids.length,
  });
  return { moved: kids.length, told };
}

/** What a bus off the road is holding: its parked children, and where each went. */
export function parkedOf(bus: Bus): { kids: Raw[]; moves: Record<string, string> } {
  const kids = Array.isArray(bus.raw.parkedRoster) ? (bus.raw.parkedRoster as Raw[]) : [];
  const moves = bus.raw.parkedRosterMoves && typeof bus.raw.parkedRosterMoves === 'object' ? (bus.raw.parkedRosterMoves as Record<string, string>) : {};
  return { kids, moves };
}

export async function backOnRoad(f: FleetModel, bus: Bus, by: { uid: string | null; email: string | null }, opts: StatusOpts = {}): Promise<{ restored: number; told: boolean }> {
  const ref = doc(db, 'buses', bus.id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('That bus is no longer on Trasse.');
  const data = snap.data() as Raw;
  const kids = Array.isArray(data.parkedRoster) ? (data.parkedRoster as Raw[]) : [];
  const moves = data.parkedRosterMoves && typeof data.parkedRosterMoves === 'object' ? (data.parkedRosterMoves as Record<string, string>) : {};
  const batch = writeBatch(db);
  if (kids.length) {
    const byHost = new Map<string, Set<string>>();
    Object.entries(moves).forEach(([cid, host]) => {
      if (!cid || !host || host === bus.id) return;
      byHost.set(host, new Set([...(byHost.get(host) || []), cid]));
    });
    for (const [host, ids] of byHost) {
      // eslint-disable-next-line no-await-in-loop
      const h = await getDoc(doc(db, 'buses', host));
      if (!h.exists()) continue;
      batch.update(doc(db, 'buses', host), { children: ((h.data().children as Raw[]) || []).filter((c) => !ids.has(str(c.childId))), updatedAt: serverTimestamp() });
    }
    await repointParents(batch, kids, () => bus.id);
  }
  batch.update(ref, {
    status: 'active',
    ...(kids.length ? { children: kids.map((c) => ({ ...c, status: 'not_picked', lastUpdated: Timestamp.now(), timestamp: null })) } : {}),
    parkedRoster: deleteField(), parkedRosterMoves: deleteField(), updatedAt: serverTimestamp(),
  });
  await batch.commit();
  void logBusStatusEvent({
    busId: bus.id, orgCode: f.org.code, plate: bus.plate, busName: bus.name, fromStatus: str(data.status) || 'maintenance', toStatus: 'active',
    byUid: by.uid, byEmail: by.email, source: opts.source || 'dashboard', childCount: kids.length,
  });
  // Told after, now that the children are back on this bus's list.
  let told = false;
  if (kids.length && opts.announce !== false) {
    const r = await announceToBusParents(f.org.code, bus.id, `${bus.name} is back on the road. Your child rides it again from the next run.`).catch(() => null);
    told = !!r?.ok;
  }
  return { restored: kids.length, told };
}
